/**
 * OCP-991 - carries a discount code from a `redeem_url` to the review step.
 *
 * The link lands on `/products?discount_code=...`, but a visitor without a
 * session goes through login or signup first, so the query string does not
 * survive the walk to the order. A per-browser convenience, nothing depends on
 * it: the server validates whatever arrives, and storage may be unavailable
 * (private window, blocked site data), so every access is guarded.
 */
const KEY = 'orboto.discount_code';
const FORMAT = /^[A-Za-z0-9]{3,64}$/;

/** Remember the code of a landing URL. Call once on load. */
export function captureDiscountCode(search: string): void {
  const code = new URLSearchParams(search).get('discount_code')?.trim();
  if (!code || !FORMAT.test(code)) return;
  try {
    window.localStorage.setItem(KEY, code.toUpperCase());
  } catch {
    // Storage blocked: the customer can still type the code.
  }
}

/**
 * OCP-994 - take `discount_code` out of the address bar once it is stored.
 * While it stays in `location.search`, every same-origin request the page makes
 * sends the full page URL as `Referer` (Referrer-Policy
 * strict-origin-when-cross-origin), and any reporter that reads the URL sees it.
 * `replaceState`: no history entry is added, the state object, the other query
 * parameters (byte for byte, no re-encoding) and the hash stay.
 */
export function stripDiscountCodeFromUrl(): void {
  try {
    const pairs = window.location.search.replace(/^\?/, '').split('&');
    const kept = pairs.filter((pair) => {
      const name = pair.split('=')[0] ?? '';
      let decoded = name;
      try {
        decoded = decodeURIComponent(name.replace(/\+/g, ' '));
      } catch {
        // keep the raw name
      }
      return decoded !== 'discount_code';
    });
    if (kept.length === pairs.length) return;
    const query = kept.filter((pair) => pair !== '').length > 0 ? `?${kept.join('&')}` : '';
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${query}${window.location.hash}`);
  } catch {
    // No history API: nothing to strip.
  }
}

/** main.tsx: remember the landing code, then remove it from the address bar. */
export function captureDiscountCodeFromLocation(): void {
  captureDiscountCode(window.location.search);
  stripDiscountCodeFromUrl();
}

export function rememberedDiscountCode(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function forgetDiscountCode(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // Nothing to clean up.
  }
}
